import React from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {alpha, SxProps, Theme} from '@mui/material/styles';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import {C41_PAGE} from '@/pages/components/audit-program/c-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface C41AccountingEstimatesViewProps {
    header: AuditProgramSectionVO;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {
    ...cellBorder,
    fontWeight: 600,
    fontSize: '0.72rem',
    py: 0.75,
    px: 0.5,
    lineHeight: 1.25,
    verticalAlign: 'bottom' as const,
};
/** Contain fields so column widths stay equal across rows. */
const bodySx = {
    ...cellBorder,
    py: 0.5,
    px: 0.5,
    verticalAlign: 'top' as const,
    overflow: 'hidden',
    width: 'auto',
};

const nativeBaseSx: SxProps<Theme> = {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    m: 0,
    border: '1px solid',
    borderColor: 'rgba(0, 0, 0, 0.23)',
    borderRadius: 1,
    px: 1,
    py: '4px',
    fontSize: (theme) => theme.typography.body2.fontSize,
    lineHeight: 1.2,
    fontFamily: 'inherit',
    color: 'text.primary',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
    outline: 'none',
    resize: 'vertical',
    '&:hover': {borderColor: 'text.primary'},
    '&:focus': {
        borderColor: 'primary.main',
        boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
    },
};

function CellInput({
    value,
    onChange,
    multiline,
    align = 'left',
}: {
    value: string;
    onChange: (v: string) => void;
    multiline?: boolean;
    align?: 'left' | 'center';
}) {
    if (multiline) {
        return (
            <Box
                component="textarea"
                value={value}
                rows={3}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
                sx={[nativeBaseSx, {textAlign: align, height: 96, minHeight: 96, resize: 'vertical'}] as SxProps<Theme>}
            />
        );
    }
    return (
        <Box
            component="input"
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            sx={[nativeBaseSx, {textAlign: align, height: 32}] as SxProps<Theme>}
        />
    );
}

/** Excel C4.1 — Review of Accounting Estimates */
export default function C41AccountingEstimatesView({
    header,
    values,
    onChange,
}: C41AccountingEstimatesViewProps) {
    const f = (id: string, k: string) => values[`${id}.${k}`] ?? '';

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a13"
                refCode="C4.1"
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.4, mb: 1, mt: 0.5}}
            >
                {C41_PAGE.title}
            </Typography>
            <Typography variant="body2" sx={{mb: 1.5, lineHeight: 1.45}}>
                {C41_PAGE.intro}
            </Typography>

            <TableContainer sx={{overflowX: 'auto'}}>
                <Table
                    size="small"
                    sx={{
                        width: '100%',
                        minWidth: 1080,
                        tableLayout: 'fixed',
                        borderCollapse: 'collapse',
                    }}
                >
                    <colgroup>
                        <col style={{width: '3%'}}/>
                        <col style={{width: '14%'}}/>
                        <col style={{width: '14%'}}/>
                        <col style={{width: '10%'}}/>
                        <col style={{width: '10%'}}/>
                        <col style={{width: '24.5%'}}/>
                        <col style={{width: '24.5%'}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx}/>
                            <TableCell sx={headSx}>{C41_PAGE.columns.estimate}</TableCell>
                            <TableCell sx={headSx}>{C41_PAGE.columns.outcome}</TableCell>
                            <TableCell sx={headSx} align="center">{C41_PAGE.columns.inherent}</TableCell>
                            <TableCell sx={headSx} align="center">{C41_PAGE.columns.control}</TableCell>
                            <TableCell sx={headSx}>{C41_PAGE.columns.overall}</TableCell>
                            <TableCell sx={headSx}>{C41_PAGE.columns.d3}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {C41_PAGE.rows.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell sx={bodySx}>
                                    <Typography variant="body2" fontWeight={600} align="center">
                                        {row.num}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(row.id, 'estimate')}
                                        onChange={(v) => onChange(`${row.id}.estimate`, v)}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(row.id, 'outcome')}
                                        onChange={(v) => onChange(`${row.id}.outcome`, v)}
                                        multiline
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(row.id, 'inherent')}
                                        onChange={(v) => onChange(`${row.id}.inherent`, v)}
                                        multiline
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(row.id, 'control')}
                                        onChange={(v) => onChange(`${row.id}.control`, v)}
                                        multiline
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(row.id, 'overall')}
                                        onChange={(v) => onChange(`${row.id}.overall`, v)}
                                        multiline
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(row.id, 'd3')}
                                        onChange={(v) => onChange(`${row.id}.d3`, v)}
                                        multiline
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{mt: 2}}>
                {C41_PAGE.notes.map((note, idx) => (
                    <Typography
                        key={idx}
                        variant="body2"
                        color="text.secondary"
                        sx={{mb: 1.25, lineHeight: 1.45, whiteSpace: 'pre-line'}}
                    >
                        {note}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
}
